<template>
  <div class="field-select-edit position-relative">
    <!-- Botón de lock/unlock para campos safe (solo si no está deshabilitado) -->
    <div v-if="field.safe && !disableLock" class="float-left">
      <button
        v-if="isLocked"
        type="button"
        class="btn btn-sm btn-outline-secondary border-0 buttonn-lock"
        @click="unlockField"
      >
        <i class="material-icons">lock</i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-primary btn-sm border-0 buttonn-lock"
        @click="lockField"
      >
        <i class="material-icons">lock_open</i>
      </button>
    </div>

    <!-- Select simple (no array) -->
    <select
      v-if="!field.isArray"
      :id="computedFieldId"
      class="form-select"
      :class="{
        'calculated-field': isLocked,
        'readonly-field': calculated || field.readonly,
      }"
      :value="displayValue"
      @change="onFieldChange($event.target.value)"
      @blur="onBlur"
      @focus="onFocus"
    >
      <option value="">{{ placeholder || "Seleccione..." }}</option>
      <option
        v-for="opt in field.options"
        :key="opt.value"
        :value="opt.value"
      >
        {{ $t(opt.label) || opt.label }}
      </option>
    </select>

    <!-- Select múltiple (array) -->
    <multiselect
      v-else
      :value="arrayValue"
      :options="field.options"
      :multiple="true"
      :close-on-select="false"
      :clear-on-select="true"
      :preserve-search="true"
      :preselect-first="false"
      :allow-empty="true"
      :searchable="true"
      :show-labels="true"
      :disabled="field.readonly || isLocked"
      label="label"
      track-by="value"
      :placeholder="placeholder || 'Seleccione una o más opciones...'"
      class="mb-2"
      @input="onArrayChange"
      @blur="onBlur"
      @focus="onFocus"
    >
      <template #selection="{ values, option }">
        <span>
          <span v-if="values.length === 0" class="multiselect__placeholder"
            >Seleccione una o más opciones</span
          >
          <span v-for="(opt, i) in values" :key="i" class="multiselect__tag">
            {{ $t(opt.label) || opt.label }}
            <span
              class="multiselect__tag-icon"
              @click.stop="onArrayChange(values.filter((o) => o.value !== opt.value))"
            ></span>
          </span>
        </span>
      </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  name: "EditFieldSelect",
  components: {
    Multiselect,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Boolean, Array],
      default: null,
    },
    fieldId: {
      type: String,
      default: null,
    },
    editingFields: {
      type: String,
      default: "",
    },
    disableLock: {
      type: Boolean,
      default: false,
    },
    calculated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activelyEditing: false,
      arrayValue: [],
    };
  },
  watch: {
    value: {
      handler(newValue) {
        if (this.field.isArray) {
          if (Array.isArray(newValue)) {
            this.arrayValue = newValue.map((val) => {
              const option = this.field.options?.find((opt) => opt.value === val);
              return option || { value: val, label: String(val) };
            });
          } else {
            this.arrayValue = [];
          }
        }
      },
      immediate: true,
    },
  },
  computed: {
    displayValue() {
      if (this.value !== null && this.value !== undefined && this.value !== "") {
        return this.value;
      }
      if (this.field.defaultValue !== undefined && this.field.defaultValue !== null) {
        return this.field.defaultValue;
      }
      return null;
    },
    placeholder() {
      return this.field.label || this.field.key || "";
    },
    computedFieldId() {
      return this.fieldId || `field-select-${this.field.key}`;
    },
    isLocked() {
      if (this.activelyEditing) {
        return false;
      }
      if (!this.value) {
        return false;
      }
      if (this.field.safe && this.editingFields !== this.field.key) {
        return true;
      }
      return false;
    },
  },
  methods: {
    onFieldChange(newValue) {
      this.activelyEditing = true;
      this.$emit("input", newValue);
    },
    onArrayChange(newArray) {
      const values = newArray.map((opt) => opt.value);
      this.arrayValue = newArray;
      this.onFieldChange(values);
    },
    onBlur() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = false;
      }
    },
    onFocus() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = true;
      }
    },
    lockField() {
      this.$emit("update:editingFields", "");
      this.activelyEditing = false;
    },
    unlockField() {
      this.$emit("update:editingFields", this.field.key);
      this.activelyEditing = true;
    },
  },
};
</script>

<style scoped>
.field-select-edit {
  width: 100%;
}

.form-select {
  padding: 0 7px !important;
}

.readonly-field {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  color: #6c757d !important;
  cursor: not-allowed !important;
  opacity: 0.8;
}

.calculated-field {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  cursor: not-allowed !important;
}

.readonly-field:focus {
  box-shadow: none !important;
  border-color: #dee2e6 !important;
}

:deep(.multiselect-container) {
  position: relative;
  z-index: 1;
}

:deep(.multiselect.is-active) {
  z-index: 99999;
}

:deep(.multiselect__content-wrapper) {
  top: 100% !important;
  bottom: auto !important;
  transform: none !important;
  z-index: 99999 !important;
  position: absolute !important;
}

:deep(.multiselect__content) {
  max-height: 200px;
  overflow-y: auto;
}
</style>

